import React, {
  useContext,
  useEffect,
  useMemo,
} from 'react';
import PatientIcon from '@mui/icons-material/PersonOutlineRounded';
import AddIcon from '@mui/icons-material/ControlPointDuplicateRounded';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import {
  useNavigate,
} from 'react-router-dom';
import {
  CalendarIcon,
  FaxIcon,
  TaskIcon,
} from 'core/icons';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
  appointmentsEventChannel,
  billingEventChannel,
  faxEventChannel,
  patientEventChannel,
  paymentsEventChannel,
  toDosEventChannel,
  visitNoteFormEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  Button,
} from 'pages/Dashboard/components/Button';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import DepositIcon from 'core/icons/deposit';
import {
  PatientDTO,
} from 'dtos';
import {
  FormCategory,
} from 'dtos/formCategory';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  UserSettingsContext,
} from 'pages/Dashboard/contexts/userSettings';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  isNilOrZero,
} from '@populateui/utils';
import {
  useDefaultPatient,
} from 'views/EMR/stores/entities';
import ActionMenu, {
  DropdownMenuItem,
} from 'core/components/DropdownMenu';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';

type Patient = PatientDTO | null | undefined;

const iconClassName = '!w-5 !h-5 !text-blue-500';

const options: DropdownMenuItem<PatientDTO | null | undefined>[] = [
  {
    id: 'add-appointment',
    label: 'Add Appointment',
    icon: CalendarIcon,
    iconClassName,
    onSelect: (patient) => {
      appointmentsEventChannel.emit('onEventCreate', {
        defaultPatient: patient as Partial<PatientDTO>,
      });
    },
  },
  {
    id: 'add-patient',
    label: 'Add Patient',
    icon: PatientIcon,
    iconClassName,
    onSelect: () => {
      patientEventChannel.emit('onCreatePatientDialog', {
        hasBackdrop: true,
        onPatientSelect: (p) => {
          patientEventChannel.emit('onPatientCreated', p);
        },
      });
    },
  },
  {
    id: 'add-todo',
    label: 'Add To-do',
    icon: TaskIcon,
    iconClassName,
    onSelect: (patient) => {
      toDosEventChannel.emit('onCreateToDo', { patient: patient as PatientCompact });
    },
  },
  {
    id: 'send-fax',
    label: 'Send Fax',
    icon: FaxIcon,
    iconClassName,
    onSelect: (patient) => {
      faxEventChannel.emit('onOpenSendFax', { patient: patient as PatientCompact });
    },
  },
  {
    id: 'collect-deposit',
    label: 'Collect Deposit',
    icon: DepositIcon,
    iconClassName,
    onSelect: (patient) => {
      paymentsEventChannel.emit('onPaymentDialogOpen', {
        patient: patient as Partial<PatientDTO>,
        isDeposit: true,
      });
    },
  },
  {
    id: 'purchase-product',
    label: 'Purchase Product',
    icon: ShoppingBasketOutlinedIcon,
    iconClassName,
    onSelect: (patient) => {
      billingEventChannel.emit('onPurchaseCreate', {
        patient: patient as Partial<PatientDTO>,
      });
    },
  },
  {
    id: 'send-forms',
    label: 'Send Forms',
    icon: DescriptionOutlinedIcon,
    iconClassName,
    onSelect: (patient) => {
      visitNoteFormEventChannel.emit('onOpenStartFormWorkflow', {
        patient: patient as PatientCompact,
      });
    },
  },
  {
    id: 'select-form',
    label: 'Orders & Referrals',
    icon: ListAltRoundedIcon,
    iconClassName,
    onSelect: (patient) => {
      visitNoteFormEventChannel.emit('onOpenStartFormWorkflow', {
        patient: patient as PatientCompact,
        categoryFilter: [FormCategory.OrderForm, FormCategory.ReferralOutgoing],
      });
    },
  },
];

export default function CreateButton() {
  const { loadingAppSettings: appIsLoading } = useContext(UserSettingsContext);
  const user = useUserInfo();
  const navigate = useNavigate();
  const patient = useDefaultPatient();

  useEffect(() => {
    const unsubscribe = patientEventChannel.on('onPatientCreated', ({ patientId }) => {
      if (!isNilOrZero(patientId)) {
        navigate(getPatientChartPath(patientId) ?? '');
      }
    });
    return () => { unsubscribe(); };
  }, []);

  const menuItems = useMemo(
    () => (user?.organization?.eFaxConfig
      ? options
      : options.filter((item) => item.id !== 'send-fax')),
    [user?.organization?.eFaxConfig],
  );

  return (
    <ActionMenu
      trigger={(
        <Button
          disabled={appIsLoading}
          variant="contained"
          color="primary"
          className="!h-8 !p-2 nw-create-button populate-magic-button !rounded-full"
          disableElevation
        >
          {appIsLoading
            ? <Spinner className="!text-white" size={24} />
            : <AddIcon className="!w-6 !h-6 !text-inherit" />}
        </Button>
      )}
      items={menuItems}
      data={patient as Patient}
      openOnHover
      paperClassName="!w-60 nw-create-options"
      placement="bottom-start"
    />
  );
}
